import { useState } from 'react'
import { FileCode, Plus, Search, Copy, Trash2 } from 'lucide-react'
import {
  useWaTemplates,
  useDeleteWaTemplate,
  useDuplicateWaTemplate,
} from '@hooks/useWaTemplates'
import { WaTemplate } from '@/types'
import { cn } from '@lib/utils'
import { CreateWaTemplateDialog } from '@components/whatsapp/CreateWaTemplateDialog'

const typeColors: Record<string, { bg: string; text: string; border: string }> = {
  TEXT: { bg: 'bg-violet-500/15', text: 'text-violet-300', border: 'border-violet-500/30' },
  IMAGE: { bg: 'bg-purple-500/15', text: 'text-purple-300', border: 'border-purple-500/30' },
  VIDEO: { bg: 'bg-pink-500/15', text: 'text-pink-300', border: 'border-pink-500/30' },
  DOCUMENT: { bg: 'bg-amber-500/15', text: 'text-amber-300', border: 'border-amber-500/30' },
  INTERACTIVE: { bg: 'bg-emerald-500/15', text: 'text-emerald-300', border: 'border-emerald-500/30' },
  QUICK_REPLY: { bg: 'bg-cyan-500/15', text: 'text-cyan-300', border: 'border-cyan-500/30' },
}

export function WaTemplatesPage() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  const { data, isLoading, error } = useWaTemplates({
    search: search || undefined,
    type: typeFilter as any || undefined,
    page,
    limit: 20,
  })

  const deleteTemplate = useDeleteWaTemplate()
  const duplicateTemplate = useDuplicateWaTemplate()

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this template?')) {
      await deleteTemplate.mutateAsync(id)
    }
  }

  const handleDuplicate = async (id: number, name: string) => {
    await duplicateTemplate.mutateAsync({ id, newName: `${name} (Copy)` })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-emerald-500/15 border border-emerald-500/30">
            <FileCode size={28} className="text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-100">WhatsApp Templates</h1>
            <p className="text-sm text-slate-400">Message templates for campaigns</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          New Template
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="input-modern pl-10"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value)
            setPage(1)
          }}
          className="input-modern"
        >
          <option value="">All Types</option>
          <option value="TEXT">Text</option>
          <option value="IMAGE">Image</option>
          <option value="VIDEO">Video</option>
          <option value="DOCUMENT">Document</option>
          <option value="INTERACTIVE">Interactive</option>
          <option value="QUICK_REPLY">Quick Reply</option>
        </select>
      </div>

      {/* Templates Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-rose-400 stat-card">
          Error loading templates. Please try again.
        </div>
      ) : !data?.data?.length ? (
        <div className="text-center py-12 stat-card">
          <FileCode size={48} className="mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400">No templates found. Create your first message template!</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.data.map((template: WaTemplate) => (
            <div
              key={template.id}
              className="stat-card hover:border-violet-500/30 transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-100 truncate">{template.name}</h3>
                </div>
                <span
                  className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ml-2',
                    typeColors[template.type || 'TEXT']?.bg || 'bg-slate-700',
                    typeColors[template.type || 'TEXT']?.text || 'text-slate-300',
                    typeColors[template.type || 'TEXT']?.border || 'border-slate-600'
                  )}
                >
                  {template.type || 'TEXT'}
                </span>
              </div>

              {/* Preview */}
              <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/50 mb-4">
                <p className="text-sm text-slate-300 line-clamp-4 whitespace-pre-wrap">
                  {template.content || 'No content'}
                </p>
              </div>

              {/* Variables */}
              {template.variables && template.variables.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs text-slate-500 mb-1.5">Variables:</div>
                  <div className="flex flex-wrap gap-1">
                    {template.variables.map((variable: any, idx: number) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-violet-500/15 text-violet-300 border border-violet-500/30"
                      >
                        {`{{${typeof variable === 'string' ? variable : variable.name}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t border-slate-700/50">
                <button
                  onClick={() => handleDuplicate(template.id, template.name)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-violet-300 bg-violet-500/15 rounded-lg hover:bg-violet-500/25 border border-violet-500/30 transition-colors"
                >
                  <Copy size={14} />
                  Copy
                </button>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-rose-400 bg-rose-500/15 rounded-lg hover:bg-rose-500/25 border border-rose-500/30 ml-auto transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between stat-card px-6 py-4">
          <div className="text-sm text-slate-400">
            Page {page} of {data.pagination.totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
              disabled={page === data.pagination.totalPages}
              className="btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Template Dialog */}
      <CreateWaTemplateDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={() => setPage(1)}
      />
    </div>
  )
}